import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ICONS } from "../../../utils/fontawesome";

const formatFullName = (client = {}) => {
  const parts = [client?.prenom, client?.nom].filter(Boolean);
  if (parts.length) return parts.join(" ");
  if (client?.email) return client.email;
  if (client?.telephone) return client.telephone;
  return "Client inconnu";
};

const formatReservationDateTime = (reservation = {}) => {
  const rawDate = reservation?.date_atelier || reservation?.date || "";
  const rawTime =
    reservation?.creneau_heure_debut ||
    reservation?.heure ||
    reservation?.creneau?.heure ||
    "";

  let dateLabel = "";
  if (rawDate) {
    const dateOnly = typeof rawDate === "string" ? rawDate.slice(0, 10) : rawDate;
    const parsed = new Date(dateOnly);
    if (!Number.isNaN(parsed.getTime())) {
      dateLabel = parsed.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
  }

  let timeLabel = "";
  if (rawTime) {
    if (typeof rawTime === "number" && Number.isFinite(rawTime)) {
      const padded = String(rawTime).padStart(2, "0");
      timeLabel = `${padded}:00`;
    } else if (typeof rawTime === "string") {
      if (/^\d{2}:\d{2}:\d{2}$/.test(rawTime)) {
        timeLabel = rawTime.slice(0, 5);
      } else if (/^\d{2}:\d{2}$/.test(rawTime)) {
        timeLabel = rawTime;
      } else if (/^\d{1,2}$/.test(rawTime.trim())) {
        timeLabel = `${rawTime.trim().padStart(2, "0")}:00`;
      }
    }
  }

  if (dateLabel && timeLabel) return `${dateLabel} • ${timeLabel}`;
  if (dateLabel) return dateLabel;
  if (timeLabel) return timeLabel;
  return "";
};

const getStatusStyles = (status) => {
  const isCancelled = String(status).toUpperCase() === "ANNULE";
  return {
    className: isCancelled
      ? "text-red-600 bg-red-100"
      : "text-green-700 bg-green-100",
    label: status || "N/A",
  };
};

const ArrowToggleButton = ({ isOpen, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
  >
    <span className="text-lg leading-none">{isOpen ? "▾" : "▸"}</span>
    {label}
  </button>
);

export const IconButton = ({ label, icon, onClick, disabled, className = "" }) => (
  <button
    type="button"
    aria-label={label}
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`px-1 rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-orange-200 ${className} ${
      disabled
        ? "border-slate-100 text-slate-300 cursor-not-allowed"
        : "border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-400"
    }`}
  >
    <FontAwesomeIcon icon={icon} className="h-4 w-4" />
  </button>
);

const ReservationList = ({
  reservations = [],
  isStaffLogged,
  loading,
  selectedHour,
  onRequestDelete,
  onRequestEdit,
  showCancelled = true,
  canAdd = false,
  addCard = null,
}) => {
  const [contactToggleMap, setContactToggleMap] = useState({});
  const [tableToggleMap, setTableToggleMap] = useState({});

  useEffect(() => {
    setContactToggleMap({});
    setTableToggleMap({});
  }, [reservations]);

  if (loading) {
    return <p className="text-slate-500">Chargement des réservations...</p>;
  }

  const sourceReservations = Array.isArray(reservations) ? reservations : [];
  const filteredReservations = showCancelled
    ? sourceReservations
    : sourceReservations.filter(
        (reservation) => String(reservation?.status).toUpperCase() !== "ANNULE"
      );

  if (!filteredReservations.length) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-slate-500">Aucune réservation pour ce créneau.</p>
        {canAdd && addCard}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {filteredReservations.map((reservation, index) => {
        const key = reservation?.creneau_id
          ? String(reservation.creneau_id)
          : `reservation-${index}`;
        const isCancelled = String(reservation?.status).toUpperCase() === "ANNULE";
        const client = reservation?.client_data ?? {};
        const fullName = formatFullName(client);
        const atelierLabel = reservation?.atelier || "Atelier inconnu";
        const zoneLabel = reservation?.block?.[0]?.zone || "Zone inconnue";
        const statusInfo = getStatusStyles(reservation?.status);
        const tablesLabel = (reservation?.block || [])
          .map((block) => block?.nom)
          .filter(Boolean)
          .join(", ") || "Aucune table précisée";

        const showContact = Boolean(
          client?.email || client?.telephone
        );

        return (
          <div
            key={key}
            className="border border-slate-200 rounded-lg p-4 flex flex-col gap-3 shadow-sm"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                {reservation?.admin_input && (
                  <span className="text-orange-600" title="Créé par un admin">
                    <FontAwesomeIcon icon={ICONS.adminInput} className="h-4 w-4" />
                  </span>
                )}
                {fullName}
              </p>
              <p className="text-xs text-slate-500">
                {formatReservationDateTime(reservation)}
              </p>
              <div className="flex items-center gap-3">
                <span
                  className={`text-xs uppercase tracking-wide px-3 py-1 rounded-full ${statusInfo.className}`}
                >
                  {statusInfo.label}
                </span>
                <div className="flex items-center gap-2">
                  <IconButton
                    label="Éditer la réservation"
                    icon={ICONS.edit}
                    onClick={() => onRequestEdit?.(reservation)}
                    disabled={isCancelled}
                  />
                  <IconButton
                    label="Supprimer la réservation"
                    icon={ICONS.trash}
                    onClick={() => onRequestDelete?.(reservation)}
                    disabled={isCancelled}
                  />
                </div>
              </div>
            </div>

            <div className="text-sm text-slate-600 space-y-1">
              <p>Atelier : <span className="font-medium text-slate-800">{atelierLabel}</span></p>
              <p>Zone : <span className="font-medium text-slate-800">{zoneLabel}</span></p>
              <p>
                Participants :{" "}
                <span className="font-medium text-slate-800">
                  {reservation?.nombre_inscrit ?? 0}
                </span>
              </p>
              {reservation?.commentaire && (
                <p>
                  Commentaire :{" "}
                  <span className="font-medium text-slate-800">
                    {reservation.commentaire}
                  </span>
                </p>
              )}
            </div>

            {showContact && (
              <div className="border-t border-slate-100 pt-3">
                <ArrowToggleButton
                  isOpen={Boolean(contactToggleMap[key])}
                  onClick={() =>
                    setContactToggleMap((prev) => ({
                      ...prev,
                      [key]: !prev?.[key],
                    }))
                  }
                  label="Coordonnées du client"
                />
                {contactToggleMap[key] && (
                  <div className="mt-2 text-sm text-slate-600 space-y-1">
                    {client.email && <p>Email : {client.email}</p>}
                    {client.telephone && <p>Téléphone : {client.telephone}</p>}
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-slate-100 pt-3">
              <ArrowToggleButton
                isOpen={Boolean(tableToggleMap[key])}
                onClick={() =>
                  setTableToggleMap((prev) => ({
                    ...prev,
                    [key]: !prev?.[key],
                  }))
                }
                label="Tables réservées"
              />
              {tableToggleMap[key] && (
                <p className="mt-2 text-sm text-slate-600">{tablesLabel}</p>
              )}
            </div>
          </div>
        );
      })}
      {canAdd && addCard}
    </div>
  );
};

export default ReservationList;
